import React from "react";
import { BsFillPaletteFill } from "react-icons/bs";
import { LucideBarChart3, LucideCheckCircle } from "lucide-react";
import { FiScissors, FiPlayCircle, FiEdit3, FiMic } from "react-icons/fi";
import { LuClipboardList, LuSparkles, LuRefreshCw, LuVideo } from "react-icons/lu";

export const videoHeroData = {
  backgroundImage: "/images/serviceBanners/VideoEditing.webp",
  whiteImage: "/images/serviceBanners/VideoEditingW.webp",
  subTitle: "Video Editing & Post Production Services",
  title: (
    <>
      High Performance <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Visual Content</span> System
    </>
  ),
  description:
    "Video editing is the process of transforming raw footage into engaging, polished, and story driven content that captures attention, builds emotion, and drives audience engagement across digital platforms.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("AI Start clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("AI Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Professional Video Editing",
      desc: "Optimized for speed and sub-second inference.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Motion Graphics Design",
      desc: "Cloud-agnostic deployments (AWS/GCP/Azure).",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Story Based Editing System",
      desc: "Enterprise-grade model governance and privacy.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Multi Platform Content ",
      desc: "Clean, maintainable, and monitorable pipelines.",
      iconColor: "#34d399",
    },
  ],
};

export const videoEditingAndProcessData = {
  buildSubtitle: "VIDEO EDITING & POST PRODUCTION",
  buildTitle: "Video Editing & Post Production Solutions for Every Business Requirement",
  buildCards: [
    {
      title: "Professional Video Editing",
      desc: "Edit raw footage into high quality videos optimized for storytelling, clarity, and engagement.",
      icon: FiScissors,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Motion Graphics & Animation",
      desc: "Create dynamic animations, transitions, and visual effects for impactful video content.",
      icon: LuSparkles,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Social Media Video Content",
      desc: "Produce short form videos, reels, and platform optimized content for maximum reach.",
      icon: FiPlayCircle,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
    {
      title: "Brand Story Video Editing",
      desc: "Craft storytelling based videos that communicate brand message effectively.",
      icon: FiEdit3,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Color Grading & Visual Enhancement",
      desc: "Enhance video quality using professional color correction and grading techniques.",
      icon: BsFillPaletteFill,
      colorClass: "text-[#ec4899]",
      bgClass: "bg-[#ec4899]/10",
      borderClass: "border-[#ec4899]/20 hover:border-[#ec4899]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(236,72,153,0.25)]",
    },
    {
      title: "Audio Sync & Sound Design",
      desc: "Improve video experience with clean audio synchronization and sound enhancement.",
      icon: FiMic,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
  ],
  processSubtitle: "VIDEO EDITING ENGINEERING FRAMEWORK",
  processTitle: "How We Build Scalable Content Production Systems",
  processSteps: [
    {
      step: "01",
      title: "Content Planning & Storyboarding",
      description: "Plan video structure, storyline, and creative direction before editing starts.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Agile Editing Workflow",
      description: "Edit content in iterative cycles for faster delivery and continuous improvement.",
      icon: LuRefreshCw,
    },
    {
      step: "03",
      title: "Video Production System",
      description: "Create structured editing workflows for reels, ads, brand videos, and long form content.",
      icon: LuVideo,
    },
    {
      step: "04",
      title: "Motion & Visual Enhancement",
      description: "Apply motion graphics, transitions, and visual effects for better engagement.",
      icon: LuSparkles,
    },
    {
      step: "05",
      title: "Performance Optimization",
      description: "Improve video retention, engagement, and platform performance using analytics.",
      icon: LucideBarChart3,
    },
    {
      step: "06",
      title: "Quality Assurance & Output Control",
      description: "Ensure high quality output with proper formatting, resolution, and platform compatibility.",
      icon: LucideCheckCircle,
    },
  ],
  benefitsSubtitle: "WHY INVEST IN VIDEO PRODUCTION SYSTEMS?",
  benefitsTitle: "Why Businesses Choose Professional Video Editing",
  benefits: [
    "High engagement visual content",
    "Better audience retention",
    "Strong brand storytelling",
    "Multi platform content optimization",
    "Increased conversion through video ads",
    "Professional brand perception",
    "Scalable content creation system",
  ],
  logoImage: "/images/serviceBanners/VideoEditingBanner.webp",
  whiteLogoImage: "/images/serviceBanners/VideoEditingW.webp",
};

// 5. Technologies Block
export const aiTechnologiesData = {
  techTitle: "OUR EXTENSIVE TOOL STACK",
  technologies: [
    { name: "TensorFlow", icon: "RestAPI" },
    { name: "PyTorch", icon: "Code" },
    { name: "OpenAI", icon: "Provider" },
    { name: "Python", icon: "Dart" },
    { name: "Docker", icon: "Vite" },
    { name: "Kubernetes", icon: "Firebase" },
  ],
  trustTitle: "TRUSTED BY INNOVATORS WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

export const videoEditingAboutData = [
  {
    title: "Video Editing Services for Engaging Digital Storytelling",
    content: (
      <>
        Video content has become one of the most effective ways for businesses to connect with audiences and communicate their message. Professional{" "}
        <b className="text-text-primary font-semibold">Video Editing Services</b> help transform raw footage into compelling visual stories that capture attention and encourage
        engagement. Many brands create video content but struggle to maintain viewer interest due to poor pacing, inconsistent visuals, or unclear messaging. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, editing strategies focus on audience engagement, storytelling flow, and content objectives to create
        videos that deliver meaningful business impact.
      </>
    ),
  },
  {
    title: "Professional Video Editing Services That Enhance Brand Communication",
    content: (
      <>
        A well-edited video can significantly improve how a brand is perceived by its audience. Reliable{" "}
        <b className="text-text-primary font-semibold">Professional Video Editing Services</b> help businesses create polished content that reflects professionalism, credibility,
        and attention to detail. Organizations investing in high-quality video production often experience stronger audience trust because visual content becomes more engaging and
        easier to understand. Tycho Technologies develops customized editing workflows that help businesses communicate their message clearly while maintaining a consistent brand
        presence across platforms.
      </>
    ),
  },
  {
    title: "Motion Graphics Services for Better Audience Engagement",
    content: (
      <>
        Visual elements play a critical role in keeping viewers interested and improving content retention. Effective{" "}
        <b className="text-text-primary font-semibold">Motion Graphics Services</b> help businesses simplify complex information, highlight key messages, and create memorable
        viewing experiences. Companies that integrate motion graphics into their content often improve audience interaction because visual storytelling becomes more dynamic and
        impactful. Strategic animation and visual enhancements help videos stand out in crowded digital environments while strengthening overall communication effectiveness.
      </>
    ),
  },
  {
    title: "Why Businesses Invest in Video Production Services",
    content: (
      <>
        The demand for <b className="text-text-primary font-semibold">Social Media Video Editing Services</b> continues to grow as businesses seek stronger engagement across
        digital channels. Companies investing in <b className="text-text-primary font-semibold">Video Production Services</b> often focus on improving audience retention,
        increasing brand awareness, and creating more effective marketing assets. Businesses partnering with Tycho Technologies gain access to customized video solutions designed
        around measurable business goals and audience preferences. A strategic approach to video content helps organizations strengthen brand perception, improve communication, and
        create long-term value through engaging visual experiences.
      </>
    ),
  },
];

export const videoEditingFaqs = [
  {
    q: "What types of videos can be improved through professional video editing services?",
    a: "Professional video editing can enhance brand videos, promotional content, social media reels, product demonstrations, corporate presentations, training videos, event highlights, and long-form marketing content by improving quality, storytelling, and viewer engagement.",
  },
  {
    q: "How does video editing impact audience retention and engagement?",
    a: "Effective video editing improves pacing, visual flow, transitions, and storytelling structure, making content more engaging and easier to follow. This helps increase watch time, audience retention, and overall content performance.",
  },
  {
    q: "Why are motion graphics important in modern video content?",
    a: "Motion graphics help explain complex ideas, emphasize important information, and create visually engaging experiences. They improve content clarity while making videos more memorable and professional across digital platforms.",
  },
  {
    q: "How does Tycho Technologies create videos optimized for different platforms?",
    a: "Tycho Technologies tailors video formats, aspect ratios, editing styles, visual elements, and content structures based on platform requirements to ensure maximum performance across social media, websites, advertising campaigns, and digital channels.",
  },
  {
    q: "Why choose Tycho Technologies for video editing and post-production services?",
    a: "Tycho Technologies combines professional editing workflows, storytelling expertise, motion graphics, visual enhancement techniques, and platform-specific optimization to create high-quality videos that improve engagement, strengthen brand communication, and support business goals.",
  },
];
